// apps/agents/src/pages/NewOrders.jsx
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import GiftSelectionModal from '../features/gifts/GiftSelectionModal'; // New modal
import LoadingSpinner from '../components/common/LoadingSpinner';

export default function NewOrders() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [selectedProductForGifts, setSelectedProductForGifts] = useState(null);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [eligibleProducts, setEligibleProducts] = useState([]);
  const [giftSelectionResult, setGiftSelectionResult] = useState(null);

  // Load all products
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const productsSnapshot = await getDocs(collection(db, 'products'));
      const productsData = [];
      productsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.isActive !== false) {
          productsData.push({
            id: doc.id,
            name: data.name || data.PrDescription || '',
            description: data.description || '',
            price: data.prices?.USD || 0,
            priceBHD: data.prices?.BHD || 0,
            imageUrl: data.imageUrl || '',
            giftLevel: data.giftLevel || null, // Important for gift eligibility
            ...data
          });
        }
      });
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if product has giftLevel
  const hasGiftLevel = (product) => {
    return product.giftLevel && Object.keys(product.giftLevel).some(key => 
      key !== 'currency' && product.giftLevel[key] > 0
    );
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id && !item.isGift);
      if (existing) {
        return prev.map(item =>
          item.id === product.id && !item.isGift
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1, isGift: false }];
    });
  };

  const removeFromCart = (productId, isGift = false) => {
    setCart(prev => prev.filter(item => !(item.id === productId && item.isGift === isGift)));
  };

  const updateQuantity = (productId, quantity, isGift = false) => {
    if (quantity <= 0) {
      removeFromCart(productId, isGift);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.id === productId && item.isGift === isGift
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setGiftSelectionResult(null);
  };

  const getCartTotal = () => {
    return cart
      .filter(item => !item.isGift)
      .reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const paidItems = cart.filter(item => !item.isGift);
  const giftItems = cart.filter(item => item.isGift);

  const handleGiftSelectionComplete = (period, selectedGiftsMap) => {
    console.log('Gift Selection Complete:', { period, selectedGiftsMap });
    
    // Store the result for display
    setGiftSelectionResult({ period, selectedGiftsMap });
    
    // Remove existing gifts for these products
    const productIds = Object.keys(selectedGiftsMap);
    const newCart = cart.filter(item => {
      if (item.isGift && productIds.includes(item.parentProductId)) {
        return false;
      }
      return true;
    });
    setCart(newCart);
    
    // Add new gifts
    for (const [productId, giftSet] of Object.entries(selectedGiftsMap)) {
      const giftItemIds = giftSet.split(' ');
      for (const itemId of giftItemIds) {
        const product = products.find(p => p.id === itemId);
        if (product) {
          const giftItem = {
            ...product,
            id: itemId,
            quantity: 1,
            isGift: true,
            parentProductId: productId,
            paymentPeriod: period,
            price: 0 // Gifts are free
          };
          setCart(prev => [...prev, giftItem]);
        }
      }
    }
    
    setShowGiftModal(false);
    setSelectedProductForGifts(null);
  };

  const openGiftModal = async (product) => {
    // Check if product has giftLevel
    if (!hasGiftLevel(product)) {
      alert('This product does not have gift options available.');
      return;
    }
    
    setSelectedProductForGifts(product);
    
    // Format eligible products for modal (just this one product with its giftLevel)
    setEligibleProducts([{
      id: product.id,
      name: product.name,
      giftLevel: product.giftLevel
    }]);
    
    setShowGiftModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 px-4 pb-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">🛍️ New Orders - Gift Selection Test</h1>
          <p className="text-gray-600">Select products, then add gifts using the new gift selection modal</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Products Grid */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Products</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {products.map(product => (
                  <div key={product.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    {product.imageUrl && (
                      <img 
                        src={product.imageUrl} 
                        alt={product.name}
                        className="w-full h-32 object-contain mb-3"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <h3 className="font-semibold text-gray-800">{product.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">${product.price} USD</p>
                    {hasGiftLevel(product) && (
                      <span className="inline-block mt-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        🎁 Gift Eligible
                      </span>
                    )}
                    <button
                      onClick={() => addToCart(product)}
                      className="w-full mt-3 bg-[#19BFB7] text-white py-2 rounded-lg hover:bg-[#158a85] transition-colors"
                    >
                      Add to Cart
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Cart Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Shopping Cart</h2>
                {getCartCount() > 0 && (
                  <button
                    onClick={clearCart}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Clear All
                  </button>
                )}
              </div>

              {/* Paid Items */}
              {paidItems.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Products ({paidItems.length})</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {paidItems.map(item => (
                      <div key={`paid-${item.id}`} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <p className="font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-gray-500">Qty: {item.quantity} × ${item.price}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1, false)}
                            className="w-6 h-6 bg-gray-200 rounded hover:bg-gray-300"
                          >
                            -
                          </button>
                          <span className="text-sm w-6 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1, false)}
                            className="w-6 h-6 bg-gray-200 rounded hover:bg-gray-300"
                          >
                            +
                          </button>
                          <button
                            onClick={() => removeFromCart(item.id, false)}
                            className="ml-2 text-red-500 text-xl"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gift Items */}
              {giftItems.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Gifts ({giftItems.length})</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {giftItems.map(item => (
                      <div key={`gift-${item.id}`} className="flex justify-between items-center p-2 bg-green-50 rounded">
                        <div>
                          <p className="font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-gray-500">FREE Gift</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id, true)}
                          className="text-red-500 text-xl"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cart Total */}
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between font-bold">
                  <span>Total:</span>
                  <span>${getCartTotal().toFixed(2)} USD</span>
                </div>
              </div>

              {/* Gift Button for each eligible product */}
              {paidItems.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h3 className="font-semibold text-gray-700">Add Gifts</h3>
                  {paidItems.map(item => (
                    hasGiftLevel(item) && (
                      <button
                        key={`gift-btn-${item.id}`}
                        onClick={() => openGiftModal(item)}
                        className="w-full bg-purple-500 text-white py-2 rounded-lg hover:bg-purple-600 transition-colors text-sm"
                      >
                        🎁 Select Gifts for {item.name}
                      </button>
                    )
                  ))}
                </div>
              )}

              {getCartCount() === 0 && (
                <p className="text-center text-gray-500 py-8">Cart is empty</p>
              )}
            </div>
          </div>
        </div>

        {/* JSON Payload Display */}
        {giftSelectionResult && (
          <div className="mt-8 bg-gray-900 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-3">📤 Payload to Next Stage:</h3>
            <pre className="text-green-400 text-sm overflow-x-auto">
              {JSON.stringify({
                period: giftSelectionResult.period,
                selectedGiftsMap: giftSelectionResult.selectedGiftsMap,
                // This is what Orders.jsx would receive
                onCompleteCall: `onComplete("${giftSelectionResult.period}", ${JSON.stringify(giftSelectionResult.selectedGiftsMap)})`
              }, null, 2)}
            </pre>
            <div className="mt-3 p-3 bg-gray-800 rounded">
              <p className="text-yellow-400 text-sm">
                ⚡ This payload will be passed to <code className="bg-gray-700 px-1 rounded">handleGiftSelectionComplete(period, selectedGiftsMap)</code><br/>
                Then gifts will be added to cart with: <code className="bg-gray-700 px-1 rounded">addGift(giftItem, 1)</code>
              </p>
            </div>
          </div>
        )}

        {/* Gift Selection Modal */}
        {showGiftModal && (
          <GiftSelectionModal
            products={eligibleProducts}
            onComplete={handleGiftSelectionComplete}
            onClose={() => {
              setShowGiftModal(false);
              setSelectedProductForGifts(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

// Helper function for getDocs (add at top with other imports)
import { collection, getDocs } from 'firebase/firestore';
